import React, { useState, useMemo } from 'react';
import { 
  Search, Package, AlertTriangle, ArrowDown, 
  Filter, Download, Calendar, ChevronRight, 
  X, Zap, Activity, RefreshCcw, MoreHorizontal,
  Plus, Minus, History
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import '../../assets/css/report.css';

const INVENTORY_STATS = [
  { label: 'Total Items', value: '1,240', change: '85 SKU', trend: 'neutral', icon: <Package size={16}/> },
  { label: 'Low Stock', value: '12', change: '+2 today', trend: 'down', icon: <AlertTriangle size={16}/> },
  { label: 'Out of Stock', value: '3', change: '-1 today', trend: 'up', icon: <X size={16}/> },
  { label: 'Turnover Rate', value: '4.2x', change: '+0.5%', trend: 'up', icon: <Activity size={16}/> },
];

const CATEGORY_CHIPS = [
  { name: 'All', count: 1240 },
  { name: 'Produce', count: 420 },
  { name: 'Meat', count: 150 },
  { name: 'Beverages', count: 650 },
  { name: 'Dry Goods', count: 20 },
];

const INVENTORY_DATA = [
  { id: 1, name: 'Buffalo Mozzarella', category: 'Dairy', stock: 15, unit: 'kg', min: 10, usage: 'high', status: 'In Stock', img: 'https://images.unsplash.com/photo-1559564484-e48b3e040ff4?auto=format&fit=crop&w=150&q=80' },
  { id: 2, name: 'Wagyu Beef Patties', category: 'Meat', stock: 8, unit: 'pcs', min: 25, usage: 'critical', status: 'Low Stock', img: 'https://images.unsplash.com/photo-1588166524941-3bf61a9c41db?auto=format&fit=crop&w=150&q=80' },
  { id: 3, name: 'San Marzano Tomatoes', category: 'Produce', stock: 45, unit: 'cans', min: 20, usage: 'medium', status: 'In Stock', img: 'https://images.unsplash.com/photo-1590515152002-3f11467008af?auto=format&fit=crop&w=150&q=80' },
  { id: 4, name: 'Craft IPA Barrels', category: 'Beverages', stock: 2, unit: 'units', min: 5, usage: 'high', status: 'Low Stock', img: 'https://images.unsplash.com/photo-1532634896-26909d0d4b89?auto=format&fit=crop&w=150&q=80' },
  { id: 5, name: 'Fresh Basil leaves', category: 'Produce', stock: 0, unit: 'kg', min: 2, usage: 'none', status: 'Out of Stock', img: 'https://images.unsplash.com/photo-1618375531912-77ac368ff30c?auto=format&fit=crop&w=150&q=80' },
  { id: 6, name: 'Truffle Oil', category: 'Dry Goods', stock: 12, unit: 'bottles', min: 3, usage: 'low', status: 'In Stock', img: 'https://images.unsplash.com/photo-1533038590840-1cde6e668a91?auto=format&fit=crop&w=150&q=80' },
];

function InventoryLogs() {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [selectedItem, setSelectedItem] = useState(null);

  const filteredInventory = useMemo(() => {
    return INVENTORY_DATA.filter(s => 
      (activeTab === 'All' || s.category === activeTab) &&
      s.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, activeTab]);

  return (
    <div className="report-container">
      <Navbar />
      
      <main className={`report-layout ${selectedItem ? 'sb-open' : ''}`}>
        <section className="report-content">
          <header className="compact-header">
            <div className="title-block">
              <div className="date-pill"><History size={14}/> Last Updated: 12m ago</div>
              <h2>Inventory <span className="accent">Intelligence</span></h2>
            </div>
            
            <div className="action-row">
              <div className="search-box">
                <Search size={14} />
                <input type="text" placeholder="Search SKU or name..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <button className="btn-secondary"><RefreshCcw size={14}/> Sync</button>
              <button className="btn-primary"><Plus size={14}/> Add Stock</button>
            </div>
          </header>

          <div className="kpi-strip">
            {INVENTORY_STATS.map((s, i) => (
              <div key={i} className="kpi-item">
                <div className="kpi-icon-wrap">{s.icon}</div>
                <div className="kpi-info">
                  <label>{s.label}</label>
                  <div className="kpi-row">
                    <span className="kpi-val">{s.value}</span>
                    <span className={`kpi-trend ${s.trend}`}>{s.change}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="category-filter-strip">
            {CATEGORY_CHIPS.map(c => (
              <button 
                key={c.name} 
                className={`filter-chip ${activeTab === c.name ? 'active' : ''}`}
                onClick={() => setActiveTab(c.name)}
              >
                {c.name} <span className="chip-count">{c.count}</span>
              </button>
            ))}
          </div>

          <div className="data-table-header">
            <span>Item Details</span>
            <span>Current Stock</span>
            <span>Usage</span>
            <span>Min. Level</span>
            <span>Status</span>
          </div>

          <div className="data-list">
            {filteredInventory.map(item => (
              <div 
                key={item.id} 
                className={`data-row ${selectedItem?.id === item.id ? 'selected' : ''} ${item.status.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => setSelectedItem(item)}
              >
                <div className="col-product">
                  <img src={item.img} alt="" className="row-thumb" />
                  <div>
                    <div className="row-name">{item.name}</div>
                    <div className="row-sub">{item.category}</div>
                  </div>
                </div>
                <div className="col-data">
                    <strong className={item.stock <= item.min ? 'text-danger' : ''}>
                        {item.stock} {item.unit}
                    </strong>
                </div>
                <div className="col-data">
                    <span className={`usage-indicator ${item.usage}`}>{item.usage}</span>
                </div>
                <div className="col-data text-muted">{item.min} {item.unit}</div>
                <div className="col-status">
                  <span className={`status-pill ${item.status.replace(/\s+/g, '-').toLowerCase()}`}>
                    {item.status}
                  </span>
                </div>
                <div className="col-action"><ChevronRight size={16}/></div>
              </div>
            ))}
          </div>
        </section>

        <aside className="report-sidebar">
          {selectedItem ? (
            <div className="sb-inner animate-fade-in">
              <div className="sb-nav">
                <button className="btn-icon" onClick={() => setSelectedItem(null)}><X size={18}/></button>
                <span className="sb-title">Stock Logistics</span>
                <button className="btn-icon"><MoreHorizontal size={18}/></button>
              </div>

              <div className="sb-stats-card">
                <img src={selectedItem.img} className="sb-img" alt="" />
                <div className="sb-info">
                  <h4>{selectedItem.name}</h4>
                  <div className="sb-badges">
                    <span className="badge">SKU: {2000 + selectedItem.id}</span>
                    <span className="badge">{selectedItem.category}</span>
                  </div>
                </div>
              </div>

              <div className="quick-adjust">
                <label>Quick Adjust Stock</label>
                <div className="adjust-controls">
                    <button className="adj-btn"><Minus size={16}/></button>
                    <div className="adj-val">{selectedItem.stock}</div>
                    <button className="adj-btn"><Plus size={16}/></button>
                </div>
              </div>

              <div className="sb-grid">
                <div className="sb-box">
                  <label>Forecasted Depletion</label>
                  <div className="sb-val">{selectedItem.stock === 0 ? 'Empty' : '2.4 Days'}</div>
                </div>
                <div className="sb-box">
                  <label>Last Replenished</label>
                  <div className="sb-val">48h ago</div>
                </div>
              </div>

              <div className="sb-section">
                <h5><Activity size={14} className="accent"/> Usage Velocity (7d)</h5>
                <div className="spark-line">
                  {[30, 45, 60, 20, 90, 85, 40].map((h, i) => (
                    <div key={i} className="spark-bar" style={{height: `${h}%`}}></div>
                  ))}
                </div>
              </div>

              <div className="order-suggestion">
                <div className="suggest-header">
                    <Zap size={16} fill="currentColor"/>
                    <span>AI Order Suggestion</span>
                </div>
                <p>Buy <strong>20 {selectedItem.unit}</strong> now to avoid weekend shortage.</p>
              </div>

              <button className="btn-full-primary">Create Purchase Order</button>
            </div>
          ) : (
            <div className="sb-empty">
              <Package size={40} className="muted-icon"/>
              <p>Select an inventory item to manage <br/>logistics and stock adjustments.</p>
            </div>
          )}
        </aside>
      </main>
    </div>
  );
}

export default InventoryLogs;